import { useRouterState } from '@tanstack/react-router'
import { LogOut, User as UserIcon } from 'lucide-react'

import { useAuth, useAuthActions } from '@/hooks/use-auth'

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

/**
 * Topbar — breadcrumb on the left, user menu on the right.
 *
 * The breadcrumb is derived from the current route — a simple
 * lookup table covers the 5-page admin (route → label).
 * Everything else falls back to the segment as-is. This is good
 * enough at MVP scale; once nested routes appear we'll wire
 * TanStack Router's matchRoute API.
 *
 * The user menu hangs off a Radix DropdownMenu (initials avatar
 * → menu with name/email + Logout). Keeps the implementation
 * desktop-y without a notification panel for now (no events to
 * notify yet).
 */

const ROUTE_LABELS: Record<string, string> = {
  planning: 'Planning',
  missions: 'Missions',
  chauffeurs: 'Chauffeurs',
  vehicules: 'Véhicules',
  clients: 'Clients',
}

export function Topbar() {
  const { user } = useAuth()
  const { logout } = useAuthActions()
  const routerState = useRouterState()
  const segments = routerState.location.pathname.split('/').filter(Boolean)

  return (
    <header
      className={[
        'sticky top-0 z-20',
        'h-14 flex items-center justify-between',
        'bg-surface border-b border-surface-border',
        'pl-6 pr-4',
      ].join(' ')}
    >
      <Breadcrumb segments={segments} />
      <UserMenu user={user} onLogout={logout} />
    </header>
  )
}

/* -------------------------------------------------------------------------- */
/*  Breadcrumb                                                                 */
/* -------------------------------------------------------------------------- */

function Breadcrumb({ segments }: { segments: string[] }) {
  // Empty path = "/" — typed routes redirect to /planning, but
  // render something sensible during the transient frame.
  if (segments.length === 0) {
    return <span className="text-sm text-text-muted">Tableau de bord</span>
  }
  return (
    <nav aria-label="Fil d'Ariane" className="flex items-center gap-2 text-sm">
      {segments.map((segment, idx) => {
        const isLast = idx === segments.length - 1
        const label = ROUTE_LABELS[segment] ?? segment
        return (
          <span key={`${segment}-${idx}`} className="flex items-center gap-2">
            {idx > 0 ? <span className="text-text-muted">/</span> : null}
            <span
              className={
                isLast ? 'font-medium text-text' : 'text-text-muted'
              }
            >
              {label}
            </span>
          </span>
        )
      })}
    </nav>
  )
}

/* -------------------------------------------------------------------------- */
/*  User menu                                                                  */
/* -------------------------------------------------------------------------- */

interface UserMenuProps {
  user: ReturnType<typeof useAuth>['user']
  onLogout: () => void
}

function UserMenu({ user, onLogout }: UserMenuProps) {
  const display = user?.name ?? user?.email ?? 'Admin'
  const initials = display
    .split(' ')
    .map((part) => part.charAt(0).toUpperCase())
    .slice(0, 2)
    .join('')

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="Menu utilisateur"
          className={[
            'inline-flex items-center gap-2 h-10 px-2 rounded-md',
            'hover:bg-surface-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand',
          ].join(' ')}
        >
          <span
            aria-hidden="true"
            className="w-8 h-8 rounded-full bg-brand text-brand-fg inline-flex items-center justify-center text-xs font-semibold"
          >
            {initials || <UserIcon size={14} />}
          </span>
          <span className="hidden sm:inline text-sm text-text">{display}</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel>{display}</DropdownMenuLabel>
        {user?.email !== undefined && user.email !== null ? (
          <DropdownMenuLabel className="!text-text-muted normal-case tracking-normal">
            {user.email}
          </DropdownMenuLabel>
        ) : null}
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => onLogout()}>
          <LogOut size={14} aria-hidden="true" />
          Se déconnecter
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
